<template>
    <Dialog v-model:open="opened">
        <DialogTrigger> <slot /> </DialogTrigger>
        <DialogContent>
            <DialogHeader class="items-center">
                <template #icon>
                    <SlackIcon />
                </template>
                <DialogTitle>
                    {{ $t("integrations.slack.modal.title") }}</DialogTitle
                >
            </DialogHeader>

            <div class="mt-2 px-6">
                <div class="mt-4 gap-1.5 grid w-full items-center">
                    <Label for="name">{{
                        $t("integrations.slack.modal.nameLabel")
                    }}</Label>
                    <Input v-model="integrationName" id="name" />
                </div>

                <div class="mt-4 gap-1.5 grid w-full items-center">
                    <Label for="oauth">{{
                        $t("integrations.slack.modal.oauth_token_label")
                    }}</Label>
                    <Input v-model="oAuthToken" id="oauth" />
                </div>

                <div class="mt-4 gap-1.5 grid w-full items-center">
                    <Label for="channel-id">{{
                        $t("integrations.slack.modal.channel_id_label")
                    }}</Label>
                    <Input v-model="channelId" id="channel-id" />
                </div>
            </div>
            <DialogFooter
                class="w-full"
                :class="cn(removable ? '!justify-between' : 'justify-end')"
            >
                <Button
                    v-if="removable"
                    @click="$emit('remove-integration')"
                    variant="destructive-ghost"
                    class="gap-1 flex"
                    size="xs"
                >
                    <Trash :size="16" />
                    {{ $t("integrations.remove_integration") }}
                </Button>

                <div class="flex-end flex">
                    <DialogClose>
                        <Button class="ml-4" variant="outline" type="button">{{
                            $t("cancel")
                        }}</Button>
                    </DialogClose>

                    <Button
                        class="ml-4"
                        type="button"
                        @click="createOrUpdateEcomailIntegration"
                        >{{
                            integration ? $t("save_changes") : $t("add")
                        }}</Button
                    >
                </div>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { cn } from "@/lib/utils";
import { ref } from "vue";
import { router } from "@inertiajs/vue3";
import { Button } from "@/components/ui/button";
import { Trash } from "lucide-vue-next";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose,
} from "@/components/ui/dialog-v2";
import SlackIcon from "../logos/simple/Slack.svg";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import route from "ziggy-js";

const props = defineProps<{
    removable?: boolean;
    integration?: any;
}>();

const emits = defineEmits(["update:modelValue", "remove-integration"]);

const integrationName = ref(
    props.integration ? props.integration.data.name : "",
);
const channelId = ref(
    props.integration ? props.integration.data.channelId : "",
);
const oAuthToken = ref(
    props.integration ? props.integration.data.oAuthToken : "",
);

const opened = ref(false);
function create() {
    router.post(
        route("app.slack-integrations.store"),
        {
            name: integrationName.value,
            channelId: channelId.value,
            oAuthToken: oAuthToken.value,
        },
        {
            onFinish: () => {
                closeModal();
            },
        },
    );
}

function update() {
    router.put(
        route("app.slack-integrations.update", {
            integration: props.integration.id,
        }),
        {
            name: integrationName.value,
            channelId: channelId.value,
            oAuthToken: oAuthToken.value,
        },
        {
            onFinish: () => {
                closeModal();
            },
        },
    );
}

function createOrUpdateEcomailIntegration() {
    if (props.integration) {
        update();
    } else {
        create();
    }
}

const closeModal = () => {
    opened.value = false;
};
</script>

<style></style>
